import { useState, useRef, useEffect } from 'react'
import { ColumnSettings } from 'handsontable/settings'
import { Button } from '@taxonomy-translation/components'
import Eye from 'images/eye.svg'
import { BottomSheet } from './bottom-sheet'
import { t } from '@lingui/core/macro'

interface ColumnSelectorProps {
  columns: ColumnSettings[]
  hiddenColumns: number[]
  onApply: (hiddenColumns: number[]) => void
  onReset: () => void
  onCancel: () => void
}

const ColumnSelector = ({
  columns,
  hiddenColumns,
  onApply,
  onReset,
  onCancel,
}: ColumnSelectorProps) => {
  const [selectedColumns, setSelectedColumns] = useState<number[]>(() => {
    const initialSelectedColumns: number[] = []
    columns.forEach((_, i) => {
      if (!hiddenColumns.includes(i)) {
        initialSelectedColumns.push(i)
      }
    })
    return initialSelectedColumns
  })

  const handleCheckboxChange = (index: number) => {
    setSelectedColumns(prevColumns => {
      if (prevColumns.includes(index)) {
        return prevColumns.filter(col => col !== index)
      }

      return [...prevColumns, index]
    })
  }

  const handleReset = () => {
    setSelectedColumns(() => columns.map((_, i) => i))
    onReset()
  }

  const handleApply = () => {
    const newHiddenColumns = columns.map((_, i) => i).filter(i => !selectedColumns.includes(i))

    onApply(newHiddenColumns.length === columns.length ? [] : newHiddenColumns)
  }

  const handleCancel = () => {
    onCancel()
  }

  return (
    <div>
      <div className="p-4 overflow-y-auto">
        <h2 className="text-lg mb-4">{t`Select columns`}</h2>
        <div className="space-y-2 h-30">
          {columns.map((col, i) => (
            <div key={col.title} className="flex items-center">
              <input
                type="checkbox"
                id={col.title}
                checked={selectedColumns.includes(i)}
                onChange={() => {
                  handleCheckboxChange(i)
                }}
                className="mr-2"
              />
              <label htmlFor={col.title}>{col.title}</label>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-between mt-4 gap-10 border-t p-4">
        <Button variant="text" onClick={handleReset}>
          {t`Reset`}
        </Button>
        <Button onClick={handleCancel} variant="outline">
          {t`Cancel`}
        </Button>
        <Button variant="primary" onClick={handleApply}>
          {t`Apply`}
        </Button>
      </div>
    </div>
  )
}

interface ColumnSelectorButtonProps {
  columns: ColumnSettings[]
  hiddenColumns: number[]
  onApply: (hiddenColumns: number[]) => void
}

export const ColumnSelectorButton = ({
  columns,
  hiddenColumns,
  onApply,
}: ColumnSelectorButtonProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleApply = (selectedColumns: number[]) => {
    onApply(selectedColumns)
    setIsDropdownOpen(false)
  }

  const handleReset = () => {
    onApply([])
  }

  const handleCancel = () => {
    setIsDropdownOpen(false)
  }

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className="relative">
      <Button
        onClick={toggleDropdown}
        className="px-6 py-2 text-[#64748B]"
        variant="outline"
        data-testid="columns-button"
      >
        <Eye />
        {t`Columns`}
      </Button>

      {isDropdownOpen && (
        <BottomSheet ref={dropdownRef} className="p-0 max-h-[520px] overflow-y-auto">
          <ColumnSelector
            columns={columns}
            hiddenColumns={hiddenColumns}
            onApply={handleApply}
            onReset={handleReset}
            onCancel={handleCancel}
          />
        </BottomSheet>
      )}
    </div>
  )
}
